import React from 'react'
import {
  ClickSvg,
  WebSvg
} from '../../components/Icons'
import { CursorArrowRaysIcon } from '@heroicons/react/20/solid'

const REPLY_TYPE = 'reply'
const ACTION_TYPE = 'action'
const BUTTON_ELEMENT = 'button'
const SUGGESTION_ELEMENT = 'suggestion'

const RBMButton = ({ text, type }) => {
  return (
    <div className="inline-flex items-center gap-2 border-2 border-indigo-500 bg-indigo-50 rounded-lg px-3 py-1.5">
      {type !== REPLY_TYPE && <WebSvg className="w-4 h-4 text-indigo-600" />}
      <span className="text-indigo-700 font-medium text-sm">{text}</span>
    </div>
  )
}

const RBMSuggestion = ({ text, type }) => {
  return (
    <div className="inline-flex items-center gap-2 border-2 border-gray-300 bg-gray-50 rounded-full px-3 py-1.5">
      {type !== REPLY_TYPE && <WebSvg className="w-4 h-4 text-gray-600" />}
      <span className="text-gray-700 font-medium text-sm">{text}</span>
    </div>
  )
}

const renderCTA = (elementInfo, elementType) => {
  const text = elementInfo?.text || elementInfo?.displayText || ''
  if (!text) return null
  
  switch (elementType) {
    case BUTTON_ELEMENT:
      return <RBMButton text={text} type={elementInfo?.type} />
    case SUGGESTION_ELEMENT:
      return <RBMSuggestion text={text} type={elementInfo?.type} />
    default:
      return null
  }
}

export const ElementClicksTag = ({ elementInfo, elementType, onClick }) => {
  // Handle different data formats - try multiple possible text fields
  let text = elementInfo?.text || 
             elementInfo?.displayText || 
             elementInfo?.reply?.text || 
             elementInfo?.action?.text ||
             (typeof elementInfo === 'string' ? elementInfo : '')
  
  // If no text, try to extract from postback
  if (!text || text.trim() === '') {
    if (elementInfo?.postback) {
      const postbackParts = elementInfo.postback.split('-')
      if (postbackParts.length > 1) {
        text = postbackParts.slice(1).join(' ').replace(/_/g, ' ')
      } else {
        text = elementInfo.postback
      }
    }
  }
  
  const clicks = elementInfo?.clicks ?? 0
  const hasClicks = clicks > 0
  const isClickable = onClick && elementInfo?.postback
  
  // Don't render if no text and no clicks (empty option)
  if (!text || text.trim() === '') {
    return null
  }
  
  const handleClick = () => {
    if (isClickable) {
      console.log('[ElementClicksTag] ✅ Clicked on option:', {
        text,
        postback: elementInfo?.postback,
        hasPostback: !!elementInfo?.postback,
        elementInfo
      })
      onClick(elementInfo)
    } else {
      console.warn('[ElementClicksTag] ⚠️ Option clicked but not clickable:', {
        text,
        hasOnClick: !!onClick,
        hasPostback: !!elementInfo?.postback,
        elementInfo
      })
    }
  }
  
  // For carousel cards, use vertical layout (title above, clicks below)
  // For other elements, use horizontal layout
  const isCarouselCard = elementType === 'button' && elementInfo?.isCarouselCard
  
  if (isCarouselCard) {
    return (
      <div 
        className={`flex flex-col gap-1 p-2 border rounded-lg transition-all duration-200 w-full ${
          hasClicks
            ? 'bg-indigo-50 border-indigo-300 shadow-sm'
            : 'bg-white border-gray-200 hover:border-indigo-200'
        } ${
          isClickable ? 'cursor-pointer hover:shadow-md' : ''
        }`}
        onClick={handleClick}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={(e) => {
          if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            handleClick()
          }
        }}
      >
        <span className={`text-sm font-medium break-words line-clamp-2 ${
          hasClicks ? 'text-indigo-900' : 'text-gray-700'
        }`} title={text}>
          {text}
        </span>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-bold tabular-nums ${
            hasClicks ? 'text-indigo-700' : 'text-gray-400'
          }`}>
            {clicks}
          </span>
          <span className="text-xs text-gray-500 font-medium">clicks</span>
          {isClickable && (
            <span className="text-xs text-indigo-600 font-medium ml-auto">
              →
            </span>
          )}
        </div>
      </div>
    )
  }
  
  return (
    <div 
      className={`flex items-center gap-2.5 p-2.5 border rounded-lg transition-all duration-200 w-full ${
        hasClicks
          ? 'bg-indigo-50 border-indigo-300 shadow-sm'
          : 'bg-white border-gray-200 hover:border-indigo-200'
      } ${
        isClickable ? 'cursor-pointer hover:shadow-md' : ''
      }`}
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          handleClick()
        }
      }}
    >
      <div className="flex-1 min-w-0">
        <span className={`text-sm font-medium break-words line-clamp-2 ${
          hasClicks ? 'text-indigo-900' : 'text-gray-700'
        }`} title={text}>
          {text}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap">
        <span className={`text-sm font-bold tabular-nums ${
          hasClicks ? 'text-indigo-700' : 'text-gray-400'
        }`}>
          {clicks}
        </span>
        <span className="text-xs text-gray-500 font-medium">clicks</span>
        {isClickable && (
          <span className="text-xs text-indigo-600 font-medium ml-0.5">
            →
          </span>
        )}
      </div>
    </div>
  )
}
